import type { IagoPortRequests } from 'common/models/iago-port-protocol';
import { type IpcToMain, UNSUPPORTED_IPC_MESSAGE } from 'common/models/ipc-messages';
import { isDev } from 'utils/client-utils';
import { recordIpcAnswer, recordIpcSend } from 'utils/pop-devtools/taps/ipc-tap';

import { createGlobalAction } from '../redux/create-slice';
import { sendIagoIngest, sendIagoInvalidateInControlPeer } from './iago-port';

type IpcWindow = Window & {
  electron?: {
    invokeIpcToMain?: (type: keyof IpcToMain, ...args: unknown[]) => Promise<unknown>;
    startPermissionAppDrag?: () => void;
  };
  sendIpc?: unknown;
  shouldLogIpc?: boolean;
};

const ipcWindow = window as IpcWindow;

// Native file drags must be initiated while Chromium is still handling the
// dragstart event, so this uses Electron's fire-and-forget IPC bridge instead
// of the Promise-based sendIpc/invoke path.
export const startPermissionAppDrag = () => ipcWindow.electron?.startPermissionAppDrag?.();

// Prefer Pop DevTools (`Pop DevTools` in the tray menu, ipc channel) for structured IPC history with durations.
// This legacy DevTools dump remains available for console-only debugging.
ipcWindow.shouldLogIpc = false;

const defaults: { [k in keyof IpcToMain]?: Awaited<ReturnType<IpcToMain[k]>> } = {
  getAppLaunchInfo: {
    didAppLaunchAfterAutoUpdate: false,
    didAppLaunchAfterUserUpdate: false,
    didAppLaunchAfterReboot: false,
    // Web/fake-desktop default: behave tray-ful (the fake desktop renders its own tray icon).
    hasTray: true,
    automation: {
      cameraOnStart: undefined,
      forceIce: undefined,
      inviteUsers: undefined,
      loopbackNetwork: false,
      role: undefined,
      skipOnboarding: false,
      forceSfu: false,
      useFakeMedia: false,
      logEncoderDataMessages: false,
      microphoneOnStart: undefined,
      fec: undefined,
      nack: undefined,
      retina: undefined,
      relayProbeOnStart: undefined,
    },
  },
  checkPermission: true,
  // An older shell (the auto-update window) doesn't know this message; `sendIpc`
  // throws on an unsupported one unless a default is registered here, and a
  // shell without the watcher simply doesn't have live permission updates.
  toggleAccessibilityPermissionWatcher: undefined,
  // A new client can briefly run against an older desktop shell during auto-update. Returning false
  // lets the permissions helper use the legacy deep-link IPC until the shell restarts.
  openSystemSettingsPane: false,
  popDevToolsGetRuntimeOptIn: false,
  // A shell without the permission placement helper resolves false, sending
  // the onboarding client down its legacy prompt fallback instead of throwing
  // during the auto-update window.
  startPermissionSettingsPlacement: false,
  stopPermissionSettingsPlacement: undefined,
  // Older shells cannot hide the real icon, which is already the correct non-tour fallback.
  setTrayVisible: undefined,
};

// biome-ignore lint/suspicious/noExplicitAny: IPC callbacks carry heterogeneous argument tuples.
export const ipcToClient = createGlobalAction<{ type: string; handlerArgs: any }>('ipcToClient');

type Awaited<T> = T extends PromiseLike<infer U> ? U : T;
type IpcRequestMap = IpcToMain & IagoPortRequests;

const nowMs = () => globalThis.performance?.now?.() ?? Date.now();

export const sendIpc = async <K extends keyof IpcRequestMap>(
  type: K,
  ...args: Parameters<IpcRequestMap[K]>
): Promise<Awaited<ReturnType<IpcRequestMap[K]>>> => {
  if (type === 'ingestIagoEvent') {
    sendIagoIngest(...(args as Parameters<IagoPortRequests['ingestIagoEvent']>));
    return undefined as Awaited<ReturnType<IpcRequestMap[K]>>;
  }
  if (type === 'invalidateInControlPeer') {
    sendIagoInvalidateInControlPeer(...(args as Parameters<IagoPortRequests['invalidateInControlPeer']>));
    return undefined as Awaited<ReturnType<IpcRequestMap[K]>>;
  }

  if (ipcWindow.shouldLogIpc) console.log('[IPC:send]', type, args);
  const startedAt = nowMs();
  recordIpcSend(type, args);
  let response: unknown;
  const ipcType = type as keyof IpcToMain;
  try {
    response = await ipcWindow.electron?.invokeIpcToMain?.(ipcType, ...args);
  } catch (error) {
    console.error(error);
    response = UNSUPPORTED_IPC_MESSAGE;
  }
  recordIpcAnswer(type, args, response, nowMs() - startedAt);
  if (ipcWindow.shouldLogIpc) console.log('[IPC:answer]', type, args, response);
  if (response === UNSUPPORTED_IPC_MESSAGE) {
    // Key-presence (not truthiness) so falsy defaults like `false` apply too.
    if (ipcType in defaults) return defaults[ipcType] as Awaited<ReturnType<IpcRequestMap[K]>>;
    throw new Error(`Unsupported ipc-to-main message: ${type}`);
  }
  return response as Awaited<ReturnType<IpcRequestMap[K]>>;
};

if (isDev) ipcWindow.sendIpc = sendIpc;
